import React from 'react';
import { Box, Button, Input, Stack, Text } from '@chakra-ui/react';
import { NavLink, useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import AuthHeader from '@/components/auth-header';
import { toaster } from '@/components/ui/toaster';
import { Field } from '@/components/ui/field';
import api from '@/utils/api';

const formSchema = z.object({
  email: z.string().min(1, { message: 'Email is required' }).email('Invalid email address'),
});

type FormValues = z.infer<typeof formSchema>;

const ForgotPassword: React.FC = () => {
  const navigate = useNavigate();

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<FormValues>({
    resolver: zodResolver(formSchema),
  });

  const onSubmit = async (data: FormValues) => {
    try {
      await api.get(`auth/reset-password/${data.email}`);

      toaster.create({
        type: 'success',
        title: 'Password reset instructions sent to your email.',
      });

      navigate('/auth/login');
    } catch (err: any) {
      toaster.create({
        type: 'error',
        title:
          err.response?.data?.error ||
          'Error: The backend API is not responding. Please try again later.',
      });
    }
  };

  return (
    <Stack gap={6}>
      <AuthHeader title='Forgot your password?' />
      <form onSubmit={handleSubmit(onSubmit)}>
        <Stack gap={4}>
          <Text fontSize={'sm'}>
            Enter your email address below and we'll send you password reset instructions.
          </Text>
          <Field label='Email' invalid={!!errors.email} errorText={errors.email?.message}>
            <Input {...register('email')} type='email' />
          </Field>
          <Button type={'submit'} width={'full'} loading={isSubmitting} loadingText='Loading...'>
            Email me a reset link
          </Button>
        </Stack>
      </form>
      <Box textAlign={'center'}>
        <Button fontSize={'sm'} variant={'ghost'} asChild>
          <NavLink to='/auth/login'>Already have an account?</NavLink>
        </Button>
      </Box>
    </Stack>
  );
};

export default ForgotPassword;
